/**
 * Location create helpers (hours + Phase 3 Zones & Playback device intent).
 * Times are interpreted in the Location's IANA timezone.
 * Zone device values are onboarding INTENT — not canonical Device records.
 */

export const WEEKDAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

export const WEEKDAY_LABELS = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
};

export const IANA_TIMEZONE_OPTIONS = [
  { value: "America/New_York", label: "Eastern" },
  { value: "America/Chicago", label: "Central" },
  { value: "America/Denver", label: "Mountain" },
  { value: "America/Los_Angeles", label: "Pacific" },
  { value: "America/Phoenix", label: "Arizona" },
  { value: "America/Anchorage", label: "Alaska" },
  { value: "Pacific/Honolulu", label: "Hawaii" },
];

/** Onboarding device-intent class values (API contract). */
export const INTENDED_DEVICE_CLASS = {
  PLAYER_11: "krosskast-player-11",
  PLAYER_8: "krosskast-player-8",
  CUSTOMER_ANDROID: "customer-android",
  CUSTOMER_APPLE: "customer-apple",
  CUSTOMER_OTHER: "customer-other",
};

/** Player 8 remains a current sold SKU (fielded through ~2027). */
export const MA_DEVICE_INTENT_OPTIONS = [
  {
    value: INTENDED_DEVICE_CLASS.PLAYER_11,
    label: "KrossKast Player 11 — Recommended / Current — full remote management",
    shortLabel: "Player 11",
  },
  {
    value: INTENDED_DEVICE_CLASS.PLAYER_8,
    label: "KrossKast Player 8 — Recommended / Current — full remote management",
    shortLabel: "Player 8",
  },
  {
    value: INTENDED_DEVICE_CLASS.CUSTOMER_ANDROID,
    label: "Customer-Owned Android Device",
    shortLabel: "Customer Android",
  },
  {
    value: INTENDED_DEVICE_CLASS.CUSTOMER_APPLE,
    label: "Customer-Owned Apple Device",
    shortLabel: "Customer Apple",
  },
  {
    value: INTENDED_DEVICE_CLASS.CUSTOMER_OTHER,
    label: "Customer-Owned Other Device",
    shortLabel: "Customer Other",
  },
];

export const CUSTOMER_OWNED_DEVICE_CLASSES = [
  INTENDED_DEVICE_CLASS.CUSTOMER_ANDROID,
  INTENDED_DEVICE_CLASS.CUSTOMER_APPLE,
  INTENDED_DEVICE_CLASS.CUSTOMER_OTHER,
];

export const ZONE_NAME_PLACEHOLDER = "e.g. Lobby, Dining Room, Patio";

export const CUSTOMER_OWNED_REMOTE_NOTE =
  "Some remote-management features may be unavailable on customer-owned devices.";
export function defaultOperatingHours() {
  return {
    monday: { open: true, openTime: "09:00", closeTime: "21:00" },
    tuesday: { open: true, openTime: "09:00", closeTime: "21:00" },
    wednesday: { open: true, openTime: "09:00", closeTime: "21:00" },
    thursday: { open: true, openTime: "09:00", closeTime: "21:00" },
    friday: { open: true, openTime: "09:00", closeTime: "22:00" },
    saturday: { open: true, openTime: "10:00", closeTime: "22:00" },
    sunday: { open: false },
  };
}

export function cloneHours(hours) {
  return JSON.parse(JSON.stringify(hours || defaultOperatingHours()));
}

export function applyMondayToWeekdays(hours) {
  const next = cloneHours(hours);
  const monday = next.monday || { open: false };
  ["tuesday", "wednesday", "thursday", "friday"].forEach((day) => {
    next[day] = monday.open
      ? { open: true, openTime: monday.openTime, closeTime: monday.closeTime }
      : { open: false };
  });
  return next;
}

export function hoursSummary(hours) {
  if (!hours) return "Hours unset";
  const openDays = WEEKDAYS.filter((d) => hours[d]?.open);
  if (openDays.length === 0) return "Closed all week";
  if (openDays.length === 7) return "Open every day";
  return `${openDays.length} days open`;
}

export function newClientRowId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `row-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function isCustomerOwnedClass(deviceClass) {
  return CUSTOMER_OWNED_DEVICE_CLASSES.includes(deviceClass);
}

export function deviceClassShortLabel(deviceClass) {
  const hit = MA_DEVICE_INTENT_OPTIONS.find((o) => o.value === deviceClass);
  return hit?.shortLabel || deviceClass || "—";
}

export function emptyZoneDraft(overrides = {}) {
  return {
    clientZoneKey: newClientRowId(),
    name: "",
    intendedDeviceClass: INTENDED_DEVICE_CLASS.PLAYER_11,
    intendedDeviceDescription: "",
    expanded: false,
    ...overrides,
  };
}

export function cloneZones(zones) {
  const list = Array.isArray(zones) && zones.length ? zones : [emptyZoneDraft()];
  return list.map((z) =>
    emptyZoneDraft({
      clientZoneKey: newClientRowId(),
      name: z.name || "",
      intendedDeviceClass:
        z.intendedDeviceClass || INTENDED_DEVICE_CLASS.PLAYER_11,
      intendedDeviceDescription: z.intendedDeviceDescription || "",
      expanded: false,
    }),
  );
}

export function zonesSummary(zones) {
  const n = Array.isArray(zones) ? zones.length : 0;
  if (n <= 0) return "0 Zones";
  return `${n} Zone${n === 1 ? "" : "s"}`;
}

export function isZoneReady(zone) {
  return Boolean(String(zone?.name || "").trim());
}

export function applyDeviceTypeToAllZones(zones, intendedDeviceClass) {
  const nextClass = intendedDeviceClass || INTENDED_DEVICE_CLASS.PLAYER_11;
  return (zones || []).map((z) => ({
    ...z,
    intendedDeviceClass: nextClass,
    intendedDeviceDescription: isCustomerOwnedClass(nextClass)
      ? z.intendedDeviceDescription || ""
      : "",
  }));
}

export function emptyLocationDraft(overrides = {}) {
  return {
    clientRowId: newClientRowId(),
    idempotencyKey: newClientRowId(),
    storeNumber: "",
    label: "",
    street: "",
    street2: "",
    city: "",
    state: "",
    zipCode: "",
    country: "US",
    timezone: "America/New_York",
    operatingHours: defaultOperatingHours(),
    zones: [emptyZoneDraft({ expanded: true })],
    expanded: false,
    status: "ready",
    statusMessage: null,
    confirmDuplicateStoreNumber: false,
    locationId: null,
    ...overrides,
  };
}

export function validateZones(zones) {
  const issues = [];
  if (!Array.isArray(zones) || zones.length < 1) {
    issues.push("At least one Zone");
    return issues;
  }
  const seen = new Set();
  zones.forEach((zone, i) => {
    const name = String(zone?.name || "").trim();
    if (!name) {
      issues.push(`Zone ${String(i + 1).padStart(2, "0")} name`);
      return;
    }
    const key = name.toLowerCase();
    if (seen.has(key)) {
      issues.push(`Duplicate Zone name "${name}"`);
    }
    seen.add(key);
  });
  return issues;
}

export function validateLocationDraft(row) {
  const missing = [];
  if (!String(row.street || "").trim()) missing.push("Address");
  if (!String(row.city || "").trim()) missing.push("City");
  if (!String(row.state || "").trim()) missing.push("State");
  if (!String(row.zipCode || "").trim()) missing.push("ZIP");
  if (!String(row.country || "").trim()) missing.push("Country");
  if (!String(row.timezone || "").trim()) missing.push("Timezone");
  if (!row.operatingHours) missing.push("Hours");
  missing.push(...validateZones(row.zones));
  return missing;
}

/** Detect duplicate Store # within a batch (case-sensitive trim). */
export function findBatchStoreDuplicates(rows) {
  const map = new Map();
  const dupes = new Set();
  for (const row of rows) {
    const sn = String(row.storeNumber || "").trim();
    if (!sn || row.status === "created") continue;
    if (map.has(sn)) {
      dupes.add(sn);
      dupes.add(map.get(sn));
    } else {
      map.set(sn, row.clientRowId);
    }
  }
  const rowIds = new Set();
  for (const row of rows) {
    const sn = String(row.storeNumber || "").trim();
    if (sn && dupes.has(sn)) rowIds.add(row.clientRowId);
  }
  return rowIds;
}

export function buildZonesPayload(zones) {
  return (zones || []).map((z, i) => {
    const intendedDeviceClass =
      z.intendedDeviceClass || INTENDED_DEVICE_CLASS.PLAYER_11;
    const payload = {
      name: String(z.name || "").trim(),
      intendedDeviceClass,
      clientZoneKey: z.clientZoneKey || `zone-${i + 1}`,
    };
    if (isCustomerOwnedClass(intendedDeviceClass)) {
      const desc = String(z.intendedDeviceDescription || "").trim();
      if (desc) payload.intendedDeviceDescription = desc;
    }
    return payload;
  });
}

export function buildCreateBody(row, { confirmDuplicateStoreNumber = false } = {}) {
  return {
    label: String(row.label || "").trim() || undefined,
    storeNumber: String(row.storeNumber || "").trim() || undefined,
    address: {
      street: String(row.street || "").trim(),
      street2: String(row.street2 || "").trim() || undefined,
      city: String(row.city || "").trim(),
      state: String(row.state || "").trim(),
      zipCode: String(row.zipCode || "").trim(),
      country: String(row.country || "").trim(),
    },
    timezone: row.timezone,
    operatingHours: row.operatingHours,
    zones: buildZonesPayload(row.zones),
    confirmDuplicateStoreNumber:
      confirmDuplicateStoreNumber || row.confirmDuplicateStoreNumber || undefined,
    idempotencyKey: row.idempotencyKey,
  };
}
